<template>
  <div class="hms-page">
    <Navigation />
    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="ts-back"><i class="mdi mdi-arrow-left"></i></a>
          <h4 class="m-0"><i class="mdi mdi-timeline-text-outline me-2"></i>Patient Life Timeline</h4>
        </div>
      </div>

      <div class="ts-intro">
        <p>Search any patient to see their full life record — from in-utero events to childhood growth to adult care. Built from every clinical module: ANC, deliveries, PNC, PMTCT, HEI, immunisations, growth, milestones, IMCI, SAM, alerts, and more.</p>
      </div>

      <!-- SEARCH BOX -->
      <div class="ts-search-box">
        <i class="mdi mdi-magnify ts-search-icon"></i>
        <input
          ref="searchInput"
          v-model="query"
          @input="onQueryChange"
          @keyup.enter="goFirst"
          placeholder="Search by name, patient number, phone, or national ID…"
          class="ts-search-input"
          autofocus />
        <i v-if="query" class="mdi mdi-close-circle ts-search-clear" @click="clearSearch"></i>
      </div>

      <!-- SEARCH RESULTS -->
      <div v-if="query.length >= 2 && !loading && !results.length" class="ts-empty">
        <i class="mdi mdi-account-search"></i>
        <p>No patients match "<strong>{{ query }}</strong>".</p>
      </div>

      <div v-if="loading" class="ts-empty"><i class="mdi mdi-loading mdi-spin"></i> Searching…</div>

      <div v-if="results.length" class="ts-results">
        <div class="ts-results-head">{{ results.length }} match{{ results.length !== 1 ? 'es' : '' }}</div>
        <div v-for="p in results" :key="p.patient_id" class="ts-result-card" @click="openTimeline(p.patient_id)">
          <div class="ts-result-avatar">
            <i :class="genderIcon(p.gender)"></i>
          </div>
          <div class="ts-result-body">
            <div class="ts-result-name">{{ p.first_name }} {{ p.last_name }} <span v-if="p.other_names" class="ts-muted">{{ p.other_names }}</span></div>
            <div class="ts-result-meta">
              <span><i class="mdi mdi-card-account-details"></i> {{ p.patient_no }}</span>
              <span><i class="mdi mdi-cake-variant"></i> {{ formatDate(p.dob) }} ({{ ageDisplay(p.dob) }})</span>
              <span><i class="mdi mdi-phone"></i> {{ p.phone || '—' }}</span>
              <span v-if="p.national_id"><i class="mdi mdi-passport"></i> {{ p.national_id }}</span>
            </div>
          </div>
          <div class="ts-result-cta">
            <i class="mdi mdi-timeline-text"></i>
            <span>Open timeline</span>
            <i class="mdi mdi-chevron-right"></i>
          </div>
        </div>
      </div>

      <!-- QUICK PICKS - demo patients seeded across the system -->
      <div v-if="!query" class="mt-4">
        <h6 class="ts-section">Quick picks (demo patients)</h6>
        <div class="ts-grid">
          <a v-for="q in quickPicks" :key="q.id" :href="'#/patient/' + q.id + '/timeline'" class="ts-quick-card" :style="{ borderLeftColor: q.color }">
            <div class="ts-quick-title">{{ q.title }}</div>
            <div class="ts-quick-desc">{{ q.desc }}</div>
            <div class="ts-quick-chips">
              <span v-for="t in q.tags" :key="t" class="ts-chip">{{ t }}</span>
            </div>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "TimelineSearch",
  components: { Navigation },
  data() {
    return {
      query: "",
      results: [],
      loading: false,
      debounceTimer: null,
      quickPicks: [
        { id: 70, color: "#EC4899", title: "Baby Cheruiyot (6 weeks old)",
          desc: "Daughter of Joyce. Full lifecycle showcase: mother's PMTCT, HEI DBS-6w negative, growth, milestones, immunizations.",
          tags: ["Newborn", "HEI", "Growth", "Milestones"] },
        { id: 71, color: "#B45309", title: "Lekishon Saitoti (18 months)",
          desc: "OTP/RUTF case — community-screened SAM, on Plumpy'Nut, weekly visits showing weight recovery.",
          tags: ["Sick visit", "SAM", "RUTF", "Recovery"] },
        { id: 6,  color: "#7C3AED", title: "Lucy Sankale (adult, PMTCT mother)",
          desc: "HIV+ on TLD, suppressed at delivery, CS, full PNC chain, baby in HEI follow-up.",
          tags: ["PMTCT", "Delivery", "Adult"] },
        { id: 67, color: "#22C55E", title: "Joyce Cheruiyot (adolescent → mother)",
          desc: "Was on adolescent register, completed pregnancy, full PNC including 6w EPDS, now parent.",
          tags: ["Adolescent", "PMTCT", "EPDS"] },
        { id: 30, color: "#1A7FE8", title: "Naomi Tipape (recent delivery)",
          desc: "SVD 2 days ago, baby's PNC ongoing, IPV screened negative, perimenopause review noted.",
          tags: ["Delivery", "PNC", "Menopause"] },
        { id: 14, color: "#DB2777", title: "Patricia Wairimu (54y, menopause)",
          desc: "Perimenopause with severe vasomotor symptoms, started on HRT today, DEXA osteopenia.",
          tags: ["Menopause", "HRT", "DEXA"] },
        { id: 22, color: "#475569", title: "Catherine Naserian (maternal death)",
          desc: "MPDSR case — died intrapartum, full 3-Delays autopsy and review process visible.",
          tags: ["Mortality", "MPDSR"] }
      ]
    };
  },
  mounted() {
    if (this.$refs.searchInput) this.$refs.searchInput.focus();
  },
  methods: {
    onQueryChange() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      if (this.query.length < 2) { this.results = []; return; }
      this.debounceTimer = setTimeout(() => { this.runSearch(); }, 220);
    },
    async runSearch() {
      this.loading = true;
      try {
        const r = await executeJson({ function: 'searchPatients', q: this.query.trim() }, hms);
        if (r.data && r.data.success) this.results = r.data.data.Patients || [];
        else this.results = [];
      } finally { this.loading = false; }
    },
    clearSearch() { this.query = ""; this.results = []; if (this.$refs.searchInput) this.$refs.searchInput.focus(); },
    openTimeline(pid) { this.$router.push('/patient/' + pid + '/timeline'); },
    goFirst() { if (this.results.length) this.openTimeline(this.results[0].patient_id); },
    genderIcon(g) { return ({ male:'mdi mdi-gender-male', female:'mdi mdi-gender-female', other:'mdi mdi-gender-non-binary' })[g] || 'mdi mdi-account'; },
    formatDate(d) { if (!d) return ''; const dt = new Date((d+'').replace(' ','T')); return isNaN(dt) ? d : dt.toLocaleDateString(); },
    ageDisplay(dob) {
      if (!dob) return '';
      const d = new Date((dob+'').replace(' ','T'));
      const now = new Date();
      const m = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
      if (m < 1) return Math.floor((now - d) / 86400000) + 'd';
      if (m < 24) return m + 'mo';
      return Math.floor(m/12) + 'y';
    }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.ts-back { color: #4a5568; font-size: 20px; text-decoration: none; }

.ts-intro { background: #f7fafc; border-left: 4px solid #2b6cb0; padding: 12px 16px; border-radius: 4px; color: #4a5568; font-size: 13px; margin-bottom: 18px; }
.ts-intro p { margin: 0; }

.ts-search-box { position: relative; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 4px 16px; display: flex; align-items: center; gap: 10px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,.04); }
.ts-search-icon { font-size: 22px; color: #a0aec0; }
.ts-search-input { flex: 1; border: none; outline: none; font-size: 16px; padding: 14px 0; color: #2d3748; background: transparent; }
.ts-search-clear { color: #a0aec0; cursor: pointer; font-size: 20px; }
.ts-search-clear:hover { color: #2d3748; }

.ts-empty { text-align: center; padding: 60px 20px; color: #a0aec0; }
.ts-empty i { font-size: 40px; }
.ts-empty p { margin-top: 8px; font-size: 14px; }

.ts-results { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
.ts-results-head { padding: 10px 16px; background: #f7fafc; font-size: 12px; color: #718096; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; border-bottom: 1px solid #e2e8f0; }
.ts-result-card { display: flex; align-items: center; gap: 14px; padding: 14px 16px; border-bottom: 1px solid #edf2f7; cursor: pointer; transition: background .12s; }
.ts-result-card:last-child { border-bottom: none; }
.ts-result-card:hover { background: #f7fafc; }
.ts-result-avatar { width: 42px; height: 42px; border-radius: 50%; background: #edf2f7; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.ts-result-avatar i { font-size: 22px; color: #4a5568; }
.ts-result-body { flex: 1; min-width: 0; }
.ts-result-name { font-size: 15px; color: #2d3748; font-weight: 600; }
.ts-muted { color: #a0aec0; font-weight: 400; font-size: 13px; }
.ts-result-meta { display: flex; flex-wrap: wrap; gap: 14px; color: #718096; font-size: 12px; margin-top: 4px; }
.ts-result-cta { color: #2b6cb0; font-size: 13px; display: flex; align-items: center; gap: 4px; font-weight: 500; white-space: nowrap; }
.ts-result-cta i { font-size: 16px; }

.ts-section { font-size: 12px; color: #4a5568; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 10px; }
.ts-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; }
.ts-quick-card { background: #fff; border: 1px solid #e2e8f0; border-left: 4px solid #2b6cb0; border-radius: 8px; padding: 14px; text-decoration: none; color: inherit; display: block; transition: transform .12s, box-shadow .12s; }
.ts-quick-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,.08); text-decoration: none; }
.ts-quick-title { font-size: 14px; font-weight: 600; color: #2d3748; margin-bottom: 6px; }
.ts-quick-desc { font-size: 12px; color: #4a5568; line-height: 1.5; margin-bottom: 10px; }
.ts-quick-chips { display: flex; flex-wrap: wrap; gap: 4px; }
.ts-chip { background: #f7fafc; color: #4a5568; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 500; }
</style>
